import { useCallback, useEffect, useRef, useState } from 'react';

const DRAG_THRESHOLD_PX = 4;

export const selectionRect = (start, current) => {
    const left = Math.min(start.x, current.x);
    const top = Math.min(start.y, current.y);
    const width = Math.abs(current.x - start.x);
    const height = Math.abs(current.y - start.y);
    return {
        left,
        top,
        right: left + width,
        bottom: top + height,
        width,
        height,
    };
};

export const rectsIntersect = (a, b) =>
    a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const isInteractiveTarget = (target) =>
    Boolean(
        target?.closest?.(
            'button, a, input, textarea, select, [role="menu"], [role="menuitem"], [role="listbox"]'
        )
    );

const isTypingTarget = (target) =>
    Boolean(target?.closest?.('input, textarea, select, [contenteditable="true"]'));

const intersectingItemIds = (container, itemAttr, rect) => {
    if (!container) return [];
    const nodes = container.querySelectorAll(`[${itemAttr}]`);
    const ids = [];
    nodes.forEach((node) => {
        const box = node.getBoundingClientRect();
        if (rectsIntersect(rect, box)) {
            const id = node.getAttribute(itemAttr);
            if (id) ids.push(id);
        }
    });
    return ids;
};

/** Union the item the drag started on so a 1-card grid still selects. */
export const idsForMarquee = (container, itemAttr, rect, startItemId) => {
    const ids = intersectingItemIds(container, itemAttr, rect);
    if (startItemId && !ids.includes(startItemId)) {
        return [startItemId, ...ids];
    }
    return ids;
};

const PAGE_SCROLL_PARENT = '.spark-native-scrollbar';
const FILTERS_BAR = '.home-page-filters-bar';

const movedPastThreshold = (start, current) => {
    const rect = selectionRect(start, current);
    return rect.width >= DRAG_THRESHOLD_PX || rect.height >= DRAG_THRESHOLD_PX;
};

/**
 * True when pointerdown is on the surface/cards, or on empty canvas around
 * them (gutters, through the bottom of the scrollport). Interactive
 * controls are skipped separately. The filters bar is a second gate.
 */
export const isMarqueeCanvasTarget = (event, container) => {
    if (!container || event?.target == null) return false;
    const { target } = event;
    if (container.contains(target)) return true;

    const scrollParent = container.closest?.(PAGE_SCROLL_PARENT);
    if (!scrollParent) return false;
    if (target !== scrollParent && !scrollParent.contains(target)) return false;

    const bounds = scrollParent.getBoundingClientRect();
    return (
        event.clientX >= bounds.left
        && event.clientX <= bounds.right
        && event.clientY >= bounds.top
        && event.clientY <= bounds.bottom
    );
};

export const findMarqueeOrigin = (container) => {
    const scrollParent = container?.closest?.(PAGE_SCROLL_PARENT);
    return scrollParent?.querySelector?.(FILTERS_BAR) || null;
};

/**
 * Marquee may start at the bottom of the filters bar or below (full width)
 * so the page title, subtitle, and filter controls stay copyable / usable.
 * No filters bar (unit tests that only mount the grid) keeps canvas starts.
 */
export const isMarqueeOriginAllowed = (event, container) => {
    const origin = findMarqueeOrigin(container);
    if (!origin) return true;
    return event.clientY >= origin.getBoundingClientRect().bottom;
};

export const shouldStartMarquee = (event, container) => (
    isMarqueeCanvasTarget(event, container) && isMarqueeOriginAllowed(event, container)
);

export const isEscapeClearBlocked = () => {
    if (isTypingTarget(document.activeElement)) return true;
    return Boolean(document.querySelector('[role="dialog"], [role="menu"]'));
};

/**
 * Drag-to-select a set of items inside `containerRef` (Cards view marquee).
 * Item nodes must carry `itemAttr` (default `data-task-key`).
 *
 * Cards themselves use `role="button"` for navigation — do not treat that as
 * interactive here or a drag on a card never starts. Pointerdown is on
 * window so a drag can start from empty canvas around the cards (gutters
 * and through the bottom of the page) anywhere below the filters bar.
 * Empty-canvas click and Escape clear the selection; the page header
 * above the filters stays copyable.
 */
export const useMarqueeSelection = ({
    containerRef,
    enabled = true,
    itemAttr = 'data-task-key',
    onSelect,
}) => {
    const [marquee, setMarquee] = useState(null);
    const startRef = useRef(null);
    const draggingRef = useRef(false);
    const additiveRef = useRef(false);
    const startedOnItemRef = useRef(false);
    const startItemIdRef = useRef(null);
    const pendingClearRef = useRef(null);
    const suppressClickRef = useRef(false);
    const onSelectRef = useRef(onSelect);
    onSelectRef.current = onSelect;

    const wasDrag = useCallback(() => {
        if (!suppressClickRef.current) return false;
        suppressClickRef.current = false;
        return true;
    }, []);

    useEffect(() => {
        const container = containerRef?.current;
        if (!enabled || !container) return undefined;

        const resetGesture = () => {
            startRef.current = null;
            draggingRef.current = false;
            startedOnItemRef.current = false;
            startItemIdRef.current = null;
            pendingClearRef.current = null;
            setMarquee(null);
        };

        const clearSelection = () => {
            onSelectRef.current?.([], { additive: false, preview: false });
        };

        const onPointerDown = (event) => {
            if (event.button !== 0) return;
            if (isInteractiveTarget(event.target)) return;
            const startItem = event.target?.closest?.(`[${itemAttr}]`);
            if (shouldStartMarquee(event, container)) {
                startRef.current = { x: event.clientX, y: event.clientY };
                draggingRef.current = false;
                additiveRef.current = event.shiftKey || event.ctrlKey || event.metaKey;
                startedOnItemRef.current = Boolean(startItem);
                startItemIdRef.current = startItem?.getAttribute?.(itemAttr) || null;
                pendingClearRef.current = null;
                return;
            }
            if (!startItem && isMarqueeCanvasTarget(event, container)) {
                pendingClearRef.current = { x: event.clientX, y: event.clientY };
            }
        };

        const onPointerMove = (event) => {
            const current = { x: event.clientX, y: event.clientY };
            if (pendingClearRef.current && movedPastThreshold(pendingClearRef.current, current)) {
                pendingClearRef.current = null;
            }
            if (!startRef.current) return;
            const rect = selectionRect(startRef.current, current);
            if (!draggingRef.current) {
                if (!movedPastThreshold(startRef.current, current)) return;
                draggingRef.current = true;
                suppressClickRef.current = true;
                window.getSelection?.()?.removeAllRanges?.();
                try {
                    container.setPointerCapture?.(event.pointerId);
                } catch {
                    // Capture is best-effort; window listeners still track the drag.
                }
            }
            if (event.cancelable) event.preventDefault();
            setMarquee(rect);
            const ids = idsForMarquee(container, itemAttr, rect, startItemIdRef.current);
            onSelectRef.current?.(ids, { additive: additiveRef.current, preview: true });
        };

        const onPointerUp = (event) => {
            if (startRef.current) {
                const startedOnItem = startedOnItemRef.current;
                if (draggingRef.current) {
                    const rect = selectionRect(startRef.current, { x: event.clientX, y: event.clientY });
                    const ids = idsForMarquee(container, itemAttr, rect, startItemIdRef.current);
                    onSelectRef.current?.(ids, { additive: additiveRef.current, preview: false });
                } else if (!startedOnItem) {
                    clearSelection();
                }
                try {
                    if (container.hasPointerCapture?.(event.pointerId)) {
                        container.releasePointerCapture(event.pointerId);
                    }
                } catch {
                    /* already released */
                }
                resetGesture();
                return;
            }
            if (pendingClearRef.current) {
                clearSelection();
                pendingClearRef.current = null;
            }
        };

        const onSelectStart = (event) => {
            if (startRef.current) event.preventDefault();
        };

        const onDragStart = (event) => {
            if (startRef.current) event.preventDefault();
        };

        // Swallow the click that completes a marquee so it cannot open a card,
        // and so wasDrag does not linger and eat the next real click.
        const onClickCapture = (event) => {
            if (!suppressClickRef.current) return;
            event.preventDefault();
            event.stopPropagation();
            suppressClickRef.current = false;
        };

        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            if (isEscapeClearBlocked()) return;
            resetGesture();
            clearSelection();
        };

        window.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('selectstart', onSelectStart);
        window.addEventListener('dragstart', onDragStart);
        window.addEventListener('click', onClickCapture, true);
        window.addEventListener('pointermove', onPointerMove, { passive: false });
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('selectstart', onSelectStart);
            window.removeEventListener('dragstart', onDragStart);
            window.removeEventListener('click', onClickCapture, true);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [containerRef, enabled, itemAttr]);

    return {
        marquee,
        isDragging: Boolean(marquee),
        wasDrag,
    };
};
